import { z } from 'zod';
import { Tag } from 'lucide-react';
import { TableTile } from '../table-tile';
import { StackedBar } from '../stacked-bar';
import { drilldowns } from '../../drilldowns';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import { cn } from '@/lib/utils';
import type { TileDefinition } from './types';

export const tasksByLabelTile: TileDefinition = {
  type: 'tasks_by_label',
  title: 'Tasks by label',
  description: 'Top labels by task volume, with open / closed split.',
  icon: Tag,
  metrics: ['tasks_by_label'],
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  configSchema: z.object({}).strict(),
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'tasks_by_label',
      from: period.from,
      to: period.to,
      enabled,
    });
    return (
      <TableTile
        columns={[
          {
            key: 'label',
            header: 'Label',
            render: (r) => r.labelName,
            getDrilldown: (r) => drilldowns.tasksByLabelRow(r, dashboardCtx),
          },
          {
            key: 'split',
            header: 'Open / Closed',
            render: (r) => (
              <div className="min-w-[80px]">
                <StackedBar left={r.openCount} right={r.closedCount} />
              </div>
            ),
          },
          {
            key: 'open',
            header: 'Open',
            align: 'right',
            render: (r) => r.openCount.toLocaleString(),
            getDrilldown: (r) => drilldowns.tasksByLabelOpen(r, dashboardCtx),
          },
          {
            key: 'closed',
            header: 'Closed',
            align: 'right',
            render: (r) => r.closedCount.toLocaleString(),
            getDrilldown: (r) => drilldowns.tasksByLabelClosed(r, dashboardCtx),
          },
          {
            key: 'pctClosed',
            header: '% Closed',
            align: 'right',
            // Red text below 80% — a label with low closure rate is
            // a bottleneck, not just a high-volume one. The visual
            // pop should pull the eye exactly there.
            render: (r) => {
              const total = r.openCount + r.closedCount;
              if (total === 0) return '—';
              const p = (r.closedCount / total) * 100;
              return (
                <span className={cn('tabular-nums', p < 80 && 'font-medium text-destructive')}>
                  {p.toFixed(0)}%
                </span>
              );
            },
          },
        ]}
        rows={metric?.items ?? []}
        rowKey={(r) => r.labelSlug}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No labeled tasks"
      />
    );
  },
};
